/**
 * usePolicyRows — TanStack Query hook for the Policen grid (Story UI-U3, AC 4).
 *
 * live mode → GET /policies via the tolerant client mapper (fetchPolicyList),
 *             then overlays Versicherer + Kunde display names from the carriers
 *             and customers lists (entity-detail overlay precedent).
 * mock mode → maps the existing POLICIES mock to PolicyListRow (mock layer NOT
 *             deleted; UI renders standalone without a backend).
 *
 * Exposes `nextCursor` so the screen can render "Mehr laden" ONLY when the
 * backend wave's pagination meta is present (null today → hidden).
 *
 * [Source: apps/web/src/features/customers/hooks/useKundeRows.ts] (hook pattern)
 * [Source: apps/web/src/features/carriers/hooks/useCarrierRows.ts] (overlay source)
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { fetchPolicyList, type PolicyListParams, type PolicyListRow } from '../api/policies-api';
import { useCarrierRows } from '../../carriers/hooks/useCarrierRows';
import { useKundeRows } from '../../customers/hooks/useKundeRows';
import { POLICIES } from '../../entity-detail/mock';

/** Map the entity-detail POLICIES mock → the PolicyListRow view-model. */
export function mockPolicyRows(): PolicyListRow[] {
  return POLICIES.map((p) => ({
    id: p.id,
    nr: p.nr,
    kunde: p.kunde,
    kundeId: p.kundeId,
    versicherer: p.versicherer,
    versichererId: p.versichererId,
    sparte: p.sparte,
    praemie: p.praemie,
    status: p.status,
    renewalTage: p.renewalTage,
    beginn: p.beginn,
  }));
}

export function usePolicyRows(filters: PolicyListParams = {}) {
  const live = isLiveData();

  const policiesQuery = useQuery({
    queryKey: qk.policies({ ...filters, mode: live ? 'live' : 'mock' }),
    queryFn: () =>
      live
        ? fetchPolicyList(filters)
        : Promise.resolve({ items: mockPolicyRows(), nextCursor: null }),
    ...(live
      ? {}
      : { initialData: () => ({ items: mockPolicyRows(), nextCursor: null }), staleTime: Infinity }),
  });

  // Name overlays (live only — mock rows already carry display names).
  const carriers = useCarrierRows();
  const customers = useKundeRows();

  const rows = useMemo<PolicyListRow[]>(() => {
    const items = policiesQuery.data?.items ?? [];
    if (!live) return items;
    const carrierName = new Map((carriers.data ?? []).map((c) => [c.id, c.name]));
    const kundeName = new Map((customers.data ?? []).map((k) => [k.id, k.name]));
    return items.map((r) => ({
      ...r,
      versicherer: (r.versichererId && carrierName.get(r.versichererId)) || r.versicherer,
      kunde: kundeName.get(r.kundeId) || r.kunde,
    }));
  }, [live, policiesQuery.data, carriers.data, customers.data]);

  return {
    rows,
    nextCursor: policiesQuery.data?.nextCursor ?? null,
    isLoading: policiesQuery.isLoading,
    isError: policiesQuery.isError,
  };
}
